/**
 * MobilePropRow - Vertical layout for prop rows on mobile
 *
 * Row 1: Stat name, line, odds, book icon, "+X books" link, EV badge
 * Row 2: Proj, True Odds, Book Win %, Model Win %, Add/Bet buttons
 *
 * This is an alternative to the horizontal scrolling table.
 * To revert, switch back to the old PropRow in StatsTable.tsx
 */
import React, { memo } from 'react';
import { Box, Stack, Typography, CircularProgress, Button } from 'omaha-design-system';
import { Plus, Minus } from '@phosphor-icons/react';
import { PlayerProp, PlayerPropSportsbook, BetSlipItem } from '../../data/types';
import { SportsbookLogo } from '../SportsbookLogo';
import { CalculateEVResponse } from '../../data/evTypes';
import { STAT_LABELS } from '../../data/statLabels';

interface MobilePropRowProps {
  prop: PlayerProp;
  playerId: string;
  customProjection: number | null;
  /** Custom EV data from recalculation API */
  customEVData?: CalculateEVResponse | null;
  /** Whether custom EV data is available */
  isCustomEV?: boolean;
  /** Whether EV calculation is in progress */
  isEVLoading?: boolean;
  /** Current bet slip to check if prop is already added */
  betSlip: BetSlipItem[];
  onAddToBetSlip: (sportsbook: PlayerPropSportsbook, customValues: {
    projection: number;
    evPct: number | null;
    modelWin: number | null;
  }) => void;
  onRemoveFromBetSlip: (betId: string) => void;
  onOpenOtherBooks: () => void;
  isLast?: boolean;
}

// Get custom EV for a specific stat/side/sportsbook from the custom EV response
const getCustomEV = (
  stat: string,
  side: 'Over' | 'Under',
  sportsbookCode: string,
  customEVData: CalculateEVResponse | null | undefined
): number | null => {
  if (!customEVData?.outcomes) return null;

  const normalized = STAT_LABELS[stat] || stat;
  const outcome = customEVData.outcomes.find(
    (o) =>
      (o.statType === stat || o.statType === normalized || STAT_LABELS[o.statType] === normalized) &&
      o.label === side
  );

  if (!outcome) return null;

  const sbResult = outcome.sportsbooks.find((sb) => sb.code === sportsbookCode);

  // API returns blendedEV as decimal (e.g., 0.051 for 5.1%), convert to percentage
  const ev = sbResult?.blendedEV;
  return ev != null ? ev * 100 : null;
};

// Get custom Model Win % (blended true win probability) from the custom EV response
const getCustomModelWin = (
  stat: string,
  side: 'Over' | 'Under',
  sportsbookCode: string,
  customEVData: CalculateEVResponse | null | undefined
): number | null => {
  if (!customEVData?.outcomes) return null;

  const normalized = STAT_LABELS[stat] || stat;
  const outcome = customEVData.outcomes.find(
    (o) =>
      (o.statType === stat || o.statType === normalized || STAT_LABELS[o.statType] === normalized) &&
      o.label === side
  );

  if (!outcome) return null;

  const sbResult = outcome.sportsbooks.find((sb) => sb.code === sportsbookCode);

  // API returns blendedTrueWinProbability as decimal (e.g., 0.55 for 55%)
  const modelWin = sbResult?.blendedTrueWinProbability;
  return modelWin ?? null;
};

const MobilePropRow: React.FC<MobilePropRowProps> = ({
  prop,
  playerId,
  customProjection,
  customEVData,
  isCustomEV,
  isEVLoading,
  betSlip,
  onAddToBetSlip,
  onRemoveFromBetSlip,
  onOpenOtherBooks,
  isLast = false,
}) => {
  const bestBook = prop.sportsbooks[0];
  const totalOtherBooks = prop.sportsbooks.length - 1;

  // Generate bet ID to check if it's in the bet slip (must be before early return for hooks rules)
  const betId = bestBook ? `${playerId}-${prop.stat}-${bestBook.book}-${bestBook.side}` : '';
  // Direct check without memoization to ensure reactivity
  const isInBetSlip = betId ? betSlip.some((bet) => bet.id === betId) : false;

  if (!bestBook) {
    return null;
  }

  const displayProjection = customProjection ?? prop.projection;

  // Get custom EV if available, otherwise use original
  const customEV = isCustomEV
    ? getCustomEV(prop.stat, bestBook.side, bestBook.book, customEVData)
    : null;
  const evPct = customEV ?? (bestBook.evPct ?? 0);
  const isPositiveEv = evPct >= 0;

  // Get custom Model Win % if available, otherwise use original
  const customModelWin = isCustomEV
    ? getCustomModelWin(prop.stat, bestBook.side, bestBook.book, customEVData)
    : null;
  const displayModelWin = customModelWin ?? bestBook.modelWin;

  const modelWinPct = displayModelWin ? displayModelWin * 100 : null;
  const bookWinPct = bestBook.bookWin ? bestBook.bookWin * 100 : null;
  const modelWin = modelWinPct ? modelWinPct.toFixed(0) : null;

  // Model Win % is green when higher than Book Win %, red when lower, neutral otherwise
  const modelWinColor = modelWinPct && bookWinPct
    ? modelWinPct > bookWinPct ? '#66BB6A' : modelWinPct < bookWinPct ? '#EF5350' : '#F2F2F3'
    : '#F2F2F3';

  // Calculate true odds from displayModelWin (our model's fair probability)
  const getTrueOdds = () => {
    if (!displayModelWin) return '-';
    if (displayModelWin >= 0.5) {
      return Math.round(-100 * displayModelWin / (1 - displayModelWin));
    }
    return `+${Math.round(100 * (1 - displayModelWin) / displayModelWin)}`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2, // 16px between rows per Figma spec
        py: 2,
        borderBottom: isLast ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      {/* Row 1: Stat, Line, Book icon, +X books, EV badge */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* Left: Stat name, line, odds, book icon, other books */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '13.33px',
              color: '#F2F2F3',
              lineHeight: 1.5,
            }}
          >
            {STAT_LABELS[prop.stat] || prop.stat}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '13.33px',
                color: '#F2F2F3',
                lineHeight: 1.5,
              }}
            >
              {bestBook.side === 'Over' ? 'o' : 'u'}{bestBook.line.toFixed(1)}
            </Typography>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '13.33px',
                color: 'rgba(242, 242, 243, 0.7)',
                lineHeight: 1.5,
              }}
            >
              {bestBook.americanOdds}
            </Typography>
            <SportsbookLogo book={bestBook.book} bookIcon={bestBook.bookIcon} size={20} borderRadius="3px" />
          </Stack>
          {totalOtherBooks > 0 && (
            <Typography
              onClick={(e) => {
                e.stopPropagation();
                onOpenOtherBooks();
              }}
              sx={{
                fontWeight: 400,
                fontSize: '13.33px',
                color: '#42A5F5',
                lineHeight: 1.5,
                cursor: 'pointer',
              }}
            >
              +{totalOtherBooks} {totalOtherBooks === 1 ? 'book' : 'books'}
            </Typography>
          )}
        </Stack>

        {/* Right: EV Chip - outlined style per Figma */}
        <Box
          sx={{
            backgroundColor: isEVLoading ? 'rgba(66, 165, 245, 0.08)' : (isPositiveEv ? 'rgba(102, 187, 106, 0.08)' : 'rgba(239, 83, 80, 0.08)'),
            border: `1px solid ${isEVLoading ? '#42A5F5' : (isPositiveEv ? '#66BB6A' : '#EF5350')}`,
            borderRadius: '6.665px',
            px: '6.665px',
            height: '26.64px',
            minWidth: isEVLoading ? 60 : 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {isEVLoading ? (
            <CircularProgress size={14} sx={{ color: '#42A5F5' }} />
          ) : (
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '13.33px',
                color: isPositiveEv ? '#66BB6A' : '#EF5350',
                lineHeight: '18px',
                letterSpacing: '0.16px',
              }}
            >
              {isPositiveEv ? '+' : ''}{evPct.toFixed(1)}% EV
            </Typography>
          )}
        </Box>
      </Box>

      {/* Row 2: Proj, True Odds, Book Win %, Model Win %, Add/Bet buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        {/* Left: Stats - flex: 1 with gap: 1 (8px) per Figma */}
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ flex: 1 }}>
          {/* Proj */}
          <Box>
            <Typography
              sx={{
                fontSize: '11.11px',
                color: 'rgba(242, 242, 243, 0.7)',
                lineHeight: 1.5,
              }}
            >
              Proj
            </Typography>
            <Typography
              sx={{
                fontSize: '13.33px',
                fontWeight: 600,
                color: '#F2F2F3',
                lineHeight: 1.5,
              }}
            >
              {displayProjection.toFixed(1)}
            </Typography>
          </Box>

          {/* True Odds */}
          <Box>
            <Typography
              sx={{
                fontSize: '11.11px',
                color: 'rgba(242, 242, 243, 0.7)',
                lineHeight: 1.5,
              }}
            >
              True Odds
            </Typography>
            <Typography
              sx={{
                fontSize: '13.33px',
                fontWeight: 400,
                color: '#F2F2F3',
                lineHeight: 1.5,
              }}
            >
              {getTrueOdds()}
            </Typography>
          </Box>

          {/* Model Win % */}
          <Box>
            <Typography
              sx={{
                fontSize: '11.11px',
                color: 'rgba(242, 242, 243, 0.7)',
                lineHeight: 1.5,
              }}
            >
              Model Win %
            </Typography>
            {isEVLoading ? (
              <CircularProgress size={14} sx={{ color: '#42A5F5' }} />
            ) : (
              <Typography
                sx={{
                  fontSize: '13.33px',
                  fontWeight: 600,
                  color: modelWinColor,
                  lineHeight: 1.5,
                }}
              >
                {modelWin ? `${modelWin}%` : '-'}
              </Typography>
            )}
          </Box>
        </Stack>

        {/* Right: Action buttons - fixed widths for alignment, ml: auto ensures right alignment */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto', flexShrink: 0 }}>
          <Button
            variant="text"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (isInBetSlip) {
                onRemoveFromBetSlip(betId);
              } else {
                onAddToBetSlip(bestBook, {
                  projection: displayProjection,
                  evPct: evPct,
                  modelWin: displayModelWin,
                });
              }
            }}
            startIcon={isInBetSlip ? <Minus size={14} weight="bold" /> : <Plus size={14} weight="bold" />}
            sx={{
              color: isInBetSlip ? '#EF5350' : '#42A5F5',
              fontSize: '11.11px',
              fontWeight: 600,
              textTransform: 'none',
              height: '38.85px',
              width: '88px',
              borderRadius: '5.555px',
              '&:hover': {
                backgroundColor: isInBetSlip ? 'rgba(239, 83, 80, 0.08)' : 'rgba(66, 165, 245, 0.08)',
              },
            }}
          >
            {isInBetSlip ? 'Remove' : 'Add'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!bestBook.deepLinkUrl}
            onClick={(e) => {
              e.stopPropagation();
              if (bestBook.deepLinkUrl) {
                window.open(bestBook.deepLinkUrl, '_blank');
              }
            }}
            sx={{
              borderColor: '#66BB6A',
              color: '#66BB6A',
              fontSize: '11.11px',
              fontWeight: 600,
              textTransform: 'none',
              height: '38.85px',
              width: '50px',
              borderRadius: '5.555px',
              '&:hover': {
                borderColor: '#4CAF50',
                backgroundColor: 'rgba(102, 187, 106, 0.08)',
              },
              '&.Mui-disabled': {
                borderColor: 'rgba(102, 187, 106, 0.3)',
                color: 'rgba(102, 187, 106, 0.5)',
              },
            }}
          >
            Bet
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default memo(MobilePropRow);
